/**
 * Guide tab classification helper.
 *
 * Each guide is classified into one of three top-level tabs based on its
 * `interval` field text and id:
 *
 *   - 'maintenance'           — interval-driven, scheduled service (oil change every 3,000 mi, etc.)
 *   - 'repair'                — event-driven, replace-when-broken work (head gasket, fuel pump, etc.)
 *   - 'road-trip-inspection'  — quick visual or sensor checks before a trip (CV joint inspection,
 *                               cooling-system pressure test, terminal cleaning, diagnostic tests)
 *
 * The classification is derived at runtime from existing guide fields so no
 * per-guide migration is required.
 */

// Explicit overrides — guides that should always be road-trip inspections
// regardless of their interval text.
const ROAD_TRIP_INSPECTION_IDS = new Set([
  'tj-pre-trip-pre-purchase-inspection',
  'tj-cv-joint-inspection',
  'tj-battery-terminals-cleaning',
  'tj-radiator-cap-pressure-test',
  'tj-fuel-pressure-test',
  'tj-alternator-output-diagnostic',
  'tj-starter-motor-diagnostic',
]);

// Repair STARTERS — if the interval text begins with one of these patterns,
// it's a reactive (event-driven) repair regardless of any incidental mileage
// mention later in the text (e.g. "...pumps last 100k miles").
const REPAIR_STARTER_PATTERNS = [
  /^replace\s+when/i,
  /^repair\s+when/i,
  /^when\s+(the\s+)?(engine|brake|fuel|fluid|trans|coolant|valve|cap|gear|wheel|wiper|seat|drum|cylinder)/i,
  /^as\s+(needed|required)/i,
  /^diagnose\s+(immediately|when)/i,
  /^reference/i,
  /^install\s+when/i,
  /^remove\s+for/i,
  /^service\s+when/i,
];

// Maintenance signals — must contain a SPECIFIC scheduled interval.
// "Inspect annually" alone is NOT enough; we require a numeric schedule
// (every X miles/years/months) or a clear scheduled-service phrase.
const MAINTENANCE_SIGNAL_PATTERNS = [
  // "every 3,000 miles", "every 50,000-80,000 miles", "every 100,000+ miles"
  /\bevery\s+\d[\d,\s\-+]*\s*miles?\b/i,
  // "every 5 years", "every 2-3 years", "every 4-6 years"
  /\bevery\s+\d[\d,\s\-+]*\s*years?\b/i,
  // "every 6-12 months", "every 3 months"
  /\bevery\s+\d[\d,\s\-+]*\s*months?\b/i,
  // "every oil change", "every tire rotation"
  /\bevery\s+(oil|tire|brake|fluid|service)\s+(change|service|rotation)\b/i,
  // "service every 30,000 miles" (covered above) — leaving "service every" alone is too loose
  // "whichever comes first" only applies if a numeric interval also appears
  // Match "every X (miles|years|months) ... whichever comes first"
  /\bevery\s+\d.*whichever\s+comes\s+first/i,
];

/**
 * Classify a guide into one of: 'maintenance' | 'repair' | 'road-trip-inspection'.
 * Returns 'repair' as the default if no other rule matches.
 * @param {object} guide
 * @returns {'maintenance'|'repair'|'road-trip-inspection'}
 */
export function getGuideTabType(guide) {
  if (!guide) return 'repair';

  // 1. Explicit road-trip-inspection overrides
  if (ROAD_TRIP_INSPECTION_IDS.has(guide.id)) {
    return 'road-trip-inspection';
  }

  const interval = (guide.interval || '').toLowerCase().trim();

  // 2. Repair starters win over any mileage mention
  for (const pattern of REPAIR_STARTER_PATTERNS) {
    if (pattern.test(interval)) return 'repair';
  }

  // 3. Maintenance: explicit scheduled language
  for (const pattern of MAINTENANCE_SIGNAL_PATTERNS) {
    if (pattern.test(interval)) return 'maintenance';
  }

  // 4. Default: Repair (event-driven)
  return 'repair';
}

/**
 * Build the list of unique `category` values from a set of guides, preserving
 * a stable order. Used to render the dynamic category-tab row.
 * @param {object[]} guides
 * @returns {string[]} sorted unique category names
 */
export function getCategoryList(guides) {
  const set = new Set();
  for (const g of guides || []) {
    if (g && g.category) set.add(g.category);
  }
  return [...set].sort();
}

/**
 * Display label and color hints for the three tab types — used by the
 * CategoryBadge component on guide cards.
 */
export const TAB_TYPE_DISPLAY = {
  maintenance: { label: 'Maintenance', badge: 'bg-blue-500/10 text-blue-400' },
  repair: { label: 'Repair', badge: 'bg-purple-500/10 text-purple-400' },
  'road-trip-inspection': { label: 'Road Trip', badge: 'bg-orange-500/10 text-orange-400' },
};

/**
 * Returns a short, card-friendly version of a guide's interval text.
 * The raw interval is often verbose (e.g. multiple clauses, off-road notes,
 * etc.); this trims to the first useful clause and caps at maxLen chars.
 *
 * @param {string} interval
 * @param {number} maxLen
 * @returns {string|null} short interval, or null if interval is empty
 */
export function getShortInterval(interval, maxLen = 80) {
  if (!interval || typeof interval !== 'string') return null;
  const trimmed = interval.trim();
  if (!trimmed) return null;
  if (trimmed.length <= maxLen) return trimmed;

  // Find first natural clause break (period, semicolon)
  const breakIdx = trimmed.search(/[.;]/);
  if (breakIdx > 10 && breakIdx <= maxLen) {
    return trimmed.slice(0, breakIdx);
  }

  // Otherwise word-boundary truncation with ellipsis
  const cut = trimmed.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  const base = lastSpace > maxLen * 0.6 ? cut.slice(0, lastSpace) : cut;
  return base + '…';
}

/**
 * Returns the right card-preview label for an interval, given the guide's
 * tab type. Maintenance items read "Every 3,000 miles"; repair items read
 * "When..." (the user-facing trigger condition); road-trip items show the
 * inspection cadence.
 */
export function getIntervalLabel(tabType) {
  if (tabType === 'maintenance') return 'Service interval';
  if (tabType === 'road-trip-inspection') return 'Inspect';
  return 'When'; // repair
}
